import { useState } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { ErrorState } from '../../components/common';
import { migrationService, type CompareDevice, type CompareReport, type ScopeView } from '../../services/migration';

const VERDICT: Record<CompareDevice['verdict'], string> = {
  agree: 'agree',
  'detect-missed': 'Detect missed alerts LogicMonitor raised',
  'detect-extra': 'Detect raised alerts LogicMonitor did not',
  both: 'both differ',
};

/** Compare: what Detect would have alerted (silently) vs what the incumbent did. Read-only. */
export function ComparePanel({ scope }: { scope: ScopeView }) {
  const { tenantId } = useTenant();
  const [days, setDays] = useState(30);
  const [report, setReport] = useState<CompareReport | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!tenantId) return;
    setBusy(true);
    setError(null);
    try {
      setReport((await migrationService.compare(tenantId, scope.id, days)).report);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const s = report?.summary;
  return (
    <details open className="card" aria-label="Compare">
      <summary><h3 style={{ display: 'inline' }}>Compare with LogicMonitor</h3></summary>
      <p className="text-muted">Read-only. Per imported device: did Detect raise an alert whenever LogicMonitor did, and vice versa? Matched by time on the device, not by sensor.</p>
      <div className="form-field-inline">
        <label htmlFor={`cmp-days-${scope.id}`}>Look back (days)</label>
        <input id={`cmp-days-${scope.id}`} type="number" min={1} max={90} value={days} onChange={(e) => setDays(Number(e.target.value))} />
        <button type="button" className="btn btn-secondary" disabled={busy || !(days >= 1 && days <= 90)} onClick={() => void run()}>Compare</button>
      </div>
      {error !== null && <ErrorState error={error} />}
      {report && s && (
        <div aria-label="Comparison report">
          <p>
            <strong>{s.devicesAgree}</strong> of {s.devicesCompared} imported device(s) agree. LogicMonitor raised {s.lmAlerts} alert(s) ({s.lmOnly} Detect did not),
            Detect raised {s.detectAlerts} ({s.detectOnly} LogicMonitor did not).
            {s.lmAlertsOnNotImported > 0 && <> {s.lmAlertsOnNotImported} LogicMonitor alert(s) were on devices that are not imported.</>}
          </p>
          {!s.complete && (
            <div className="notice" role="alert">
              Partial result: {report.notes.filter((n) => !n.startsWith('Alerts are matched')).join(' ')}
              {s.devicesNotCompared > 0 && <> {s.devicesNotCompared} device(s) were not compared.</>}
            </div>
          )}
          {report.devices.length > 0 && (
            <div className="table-scroll">
              <table className="data-table">
                <thead><tr><th>LM device</th><th>Verdict</th><th>LM alerts</th><th>Detect alerts</th><th>Only one side</th></tr></thead>
                <tbody>
                  {report.devices.map((d) => (
                    <tr key={d.lmDeviceId}>
                      <td>{d.lmDeviceId}{d.comparedFrom && <div className="text-muted">compared from {new Date(d.comparedFrom).toLocaleDateString()}</div>}</td>
                      <td>{VERDICT[d.verdict]}</td>
                      <td>{d.lmAlerts}</td>
                      <td>{d.detectAlerts}</td>
                      <td>
                        {d.lmOnlyExamples.length > 0 && (
                          <div>LogicMonitor only: {d.lmOnly}
                            <ul>{d.lmOnlyExamples.map((e, i) => <li key={i}>{e.what} — {new Date(e.startedAt).toLocaleString()}{e.ongoing ? ' (ongoing)' : ''}</li>)}</ul>
                          </div>
                        )}
                        {d.detectOnlyExamples.length > 0 && (
                          <div>Detect only: {d.detectOnly}
                            <ul>{d.detectOnlyExamples.map((e, i) => <li key={i}>{e.what} — {new Date(e.startedAt).toLocaleString()}{e.ongoing ? ' (ongoing)' : ''}</li>)}</ul>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {report.devicesTotalDivergent > report.devices.length && <p className="text-muted">Showing the {report.devices.length} most divergent of {report.devicesTotalDivergent}.</p>}
        </div>
      )}
    </details>
  );
}
